import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Save,
  RefreshCw,
  FileText,
  Clock,
  Eye,
  Download,
  FileJson,
  FileSpreadsheet,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import {
  useEncryptionPolicy,
  useUpsertEncryptionPolicy,
  useComplianceStatus,
  useEncryptionAuditLog,
  useReviewAuditEntry,
  ComplianceMode,
  COMPLIANCE_LABELS,
  COMPLIANCE_DESCRIPTIONS,
  AuditLogEntry,
} from "@/hooks/useEncryptionPolicies";

interface ComplianceSettingsProps {
  tenantId?: string | null;
}

const SEVERITY_COLORS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  info: "secondary",
  warning: "outline",
  critical: "destructive",
};

function AuditLogTable({
  entries,
  onReview,
  isReviewing,
}: {
  entries: AuditLogEntry[];
  onReview: (id: string) => void;
  isReviewing: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead>Actor</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="whitespace-nowrap">
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {format(new Date(entry.created_at), "MMM d, HH:mm")}
                </span>
              </TableCell>
              <TableCell>
                <span className="font-medium">{entry.event_type.replace(/_/g, " ")}</span>
              </TableCell>
              <TableCell>
                <Badge variant={SEVERITY_COLORS[entry.event_severity]}>
                  {entry.event_severity}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {entry.actor_email || "System"}
              </TableCell>
              <TableCell>
                {entry.requires_review ? (
                  entry.reviewed_at ? (
                    <Badge variant="outline" className="text-green-600">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Reviewed
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Needs Review
                    </Badge>
                  )
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                {entry.requires_review && !entry.reviewed_at && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onReview(entry.id)}
                    disabled={isReviewing}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Review
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function ComplianceSettings({ tenantId = null }: ComplianceSettingsProps) {
  const { data: policy, isLoading: policyLoading, refetch: refetchPolicy } = useEncryptionPolicy(tenantId);
  const { data: compliance, isLoading: complianceLoading, refetch: refetchCompliance } = useComplianceStatus(tenantId);
  const { data: auditLog, isLoading: auditLoading, refetch: refetchAudit } = useEncryptionAuditLog({
    tenant_id: tenantId,
    limit: 20,
  });
  const upsertMutation = useUpsertEncryptionPolicy();
  const reviewMutation = useReviewAuditEntry();

  // Show loading while fetching initial data
  const isLoading = policyLoading || complianceLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Use a key to reset the inner form when policy changes
  return (
    <ComplianceSettingsInner
      key={policy?.id ?? "new"}
      policy={policy}
      tenantId={tenantId}
      compliance={compliance}
      auditLog={auditLog}
      auditLoading={auditLoading}
      upsertMutation={upsertMutation}
      reviewMutation={reviewMutation}
      refetchPolicy={refetchPolicy}
      refetchCompliance={refetchCompliance}
      refetchAudit={refetchAudit}
    />
  );
}

interface ComplianceSettingsInnerProps {
  policy: ReturnType<typeof useEncryptionPolicy>["data"];
  tenantId: string | null;
  compliance: ReturnType<typeof useComplianceStatus>["data"];
  auditLog: ReturnType<typeof useEncryptionAuditLog>["data"];
  auditLoading: boolean;
  upsertMutation: ReturnType<typeof useUpsertEncryptionPolicy>;
  reviewMutation: ReturnType<typeof useReviewAuditEntry>;
  refetchPolicy: () => void;
  refetchCompliance: () => void;
  refetchAudit: () => void;
}

// Export helper functions
function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportComplianceReportJSON(
  policy: ReturnType<typeof useEncryptionPolicy>["data"],
  compliance: ReturnType<typeof useComplianceStatus>["data"],
  auditLog: ReturnType<typeof useEncryptionAuditLog>["data"]
) {
  const report = {
    report_type: "Encryption Compliance Report",
    generated_at: new Date().toISOString(),
    policy: policy ? {
      compliance_mode: policy.compliance_mode,
      require_key_escrow: policy.require_key_escrow,
      require_dual_control: policy.require_dual_control,
      require_audit_logging: policy.require_audit_logging,
      allow_key_export: policy.allow_key_export,
      require_hsm: policy.require_hsm,
      min_key_length_bits: policy.min_key_length_bits,
      rotation_period_days: policy.rotation_period_days,
      approved_algorithms: policy.approved_algorithms,
      enforcement_mode: policy.enforcement_mode,
    } : null,
    compliance_status: compliance ? {
      is_compliant: compliance.is_compliant,
      compliance_mode: compliance.compliance_mode,
      key_rotation_status: compliance.key_rotation_status,
      days_until_rotation_required: compliance.days_until_rotation_required,
      issues: compliance.issues,
    } : null,
    audit_log_summary: {
      total_entries: auditLog?.length ?? 0,
      pending_review: auditLog?.filter(e => e.requires_review && !e.reviewed_at).length ?? 0,
      critical_events: auditLog?.filter(e => e.event_severity === 'critical').length ?? 0,
      warning_events: auditLog?.filter(e => e.event_severity === 'warning').length ?? 0,
    },
    recent_audit_entries: auditLog?.slice(0, 50).map(entry => ({
      id: entry.id,
      event_type: entry.event_type,
      event_severity: entry.event_severity,
      actor_email: entry.actor_email,
      requires_review: entry.requires_review,
      reviewed_at: entry.reviewed_at,
      created_at: entry.created_at,
    })) ?? [],
  };

  const filename = `compliance-report-${format(new Date(), 'yyyy-MM-dd-HHmm')}.json`;
  downloadFile(JSON.stringify(report, null, 2), filename, 'application/json');
}

function exportComplianceReportCSV(
  policy: ReturnType<typeof useEncryptionPolicy>["data"],
  compliance: ReturnType<typeof useComplianceStatus>["data"],
  auditLog: ReturnType<typeof useEncryptionAuditLog>["data"]
) {
  // Build CSV content
  const lines: string[] = [];

  // Header section
  lines.push('Encryption Compliance Report');
  lines.push(`Generated:,${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`);
  lines.push('');

  // Policy section
  lines.push('Policy Settings');
  lines.push('Setting,Value');
  if (policy) {
    lines.push(`Compliance Mode,${policy.compliance_mode}`);
    lines.push(`Enforcement Mode,${policy.enforcement_mode || 'warn'}`);
    lines.push(`Require Key Escrow,${policy.require_key_escrow}`);
    lines.push(`Require Dual Control,${policy.require_dual_control}`);
    lines.push(`Require Audit Logging,${policy.require_audit_logging}`);
    lines.push(`Allow Key Export,${policy.allow_key_export}`);
    lines.push(`Require HSM,${policy.require_hsm}`);
    lines.push(`Min Key Length (bits),${policy.min_key_length_bits || 'N/A'}`);
    lines.push(`Rotation Period (Days),${policy.rotation_period_days || 'N/A'}`);
  }
  lines.push('');

  // Compliance Status section
  lines.push('Compliance Status');
  lines.push('Metric,Value');
  if (compliance) {
    lines.push(`Is Compliant,${compliance.is_compliant}`);
    lines.push(`Key Rotation Status,${compliance.key_rotation_status || 'N/A'}`);
    lines.push(`Days Until Rotation,${compliance.days_until_rotation_required ?? 'N/A'}`);
    if (compliance.issues && compliance.issues.length > 0) {
      lines.push(`Issues Found,${compliance.issues.length}`);
      compliance.issues.forEach((issue, idx) => {
        // Handle both string and object issue formats
        const issueText = typeof issue === 'string' ? issue : (issue as any).message || String(issue);
        lines.push(`Issue ${idx + 1},"${issueText.replace(/"/g, '""')}"`);
      });
    } else {
      lines.push('Issues Found,0');
    }
  }
  lines.push('');

  // Audit Log section
  lines.push('Audit Log Summary');
  lines.push(`Total Entries,${auditLog?.length ?? 0}`);
  lines.push(`Pending Review,${auditLog?.filter(e => e.requires_review && !e.reviewed_at).length ?? 0}`);
  lines.push(`Critical Events,${auditLog?.filter(e => e.event_severity === 'critical').length ?? 0}`);
  lines.push(`Warning Events,${auditLog?.filter(e => e.event_severity === 'warning').length ?? 0}`);
  lines.push('');

  // Audit entries
  lines.push('Recent Audit Entries');
  lines.push('Timestamp,Event Type,Severity,Actor,Requires Review,Reviewed');
  auditLog?.slice(0, 50).forEach(entry => {
    lines.push([
      format(new Date(entry.created_at), 'yyyy-MM-dd HH:mm:ss'),
      entry.event_type,
      entry.event_severity,
      entry.actor_email || 'System',
      entry.requires_review ? 'Yes' : 'No',
      entry.reviewed_at ? format(new Date(entry.reviewed_at), 'yyyy-MM-dd HH:mm') : 'N/A',
    ].join(','));
  });

  const filename = `compliance-report-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
  downloadFile(lines.join('\n'), filename, 'text/csv');
}

function ComplianceSettingsInner({
  policy,
  tenantId,
  compliance,
  auditLog,
  auditLoading,
  upsertMutation,
  reviewMutation,
  refetchPolicy,
  refetchCompliance,
  refetchAudit,
}: ComplianceSettingsInnerProps) {
  // Form state initialized from policy data
  const [complianceMode, setComplianceMode] = useState<ComplianceMode>(
    policy?.compliance_mode ?? "none"
  );
  const [requireKeyEscrow, setRequireKeyEscrow] = useState(policy?.require_key_escrow ?? false);
  const [requireDualControl, setRequireDualControl] = useState(policy?.require_dual_control ?? false);
  const [requireAuditLogging, setRequireAuditLogging] = useState(policy?.require_audit_logging ?? true);
  const [allowKeyExport, setAllowKeyExport] = useState(policy?.allow_key_export ?? false);
  const [requireHsm, setRequireHsm] = useState(policy?.require_hsm ?? false);

  const handleSave = async () => {
    // Validate compliance requirements
    if (complianceMode !== "none" && !requireAuditLogging) {
      toast.error("Audit logging is required for compliance modes");
      return;
    }

    try {
      await upsertMutation.mutateAsync({
        tenant_id: tenantId,
        compliance_mode: complianceMode,
        require_key_escrow: requireKeyEscrow,
        require_dual_control: requireDualControl,
        require_audit_logging: requireAuditLogging,
        allow_key_export: allowKeyExport,
        require_hsm: requireHsm,
      });
      toast.success("Compliance settings saved successfully");
      refetchCompliance();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save settings");
    }
  };

  const handleReviewEntry = async (id: string) => {
    try {
      await reviewMutation.mutateAsync(id);
      toast.success("Audit entry marked as reviewed");
      refetchAudit();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to review entry");
    }
  };

  const handleRefresh = () => {
    refetchPolicy();
    refetchCompliance();
    refetchAudit();
    toast.success("Data refreshed");
  };

  const handleExportJSON = () => {
    exportComplianceReportJSON(policy, compliance, auditLog);
    toast.success("Compliance report exported as JSON");
  };

  const handleExportCSV = () => {
    exportComplianceReportCSV(policy, compliance, auditLog);
    toast.success("Compliance report exported as CSV");
  };

  const pendingReviewCount = auditLog?.filter((e) => e.requires_review && !e.reviewed_at).length ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium">Compliance Settings</h3>
          <p className="text-sm text-muted-foreground">
            Configure compliance mode and security requirements
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export Report
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportJSON}>
                <FileJson className="h-4 w-4 mr-2" />
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <FileText className="h-4 w-4 mr-2" />
                Export as PDF (Coming Soon)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Compliance Status */}
      {compliance && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {compliance.is_compliant ? (
                <ShieldCheck className="h-5 w-5 text-green-600" />
              ) : (
                <ShieldAlert className="h-5 w-5 text-destructive" />
              )}
              Compliance Status
            </CardTitle>
            <CardDescription>
              Current compliance status for {COMPLIANCE_LABELS[compliance.compliance_mode]}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Status</span>
              {compliance.is_compliant ? (
                <Badge className="bg-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Compliant
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <XCircle className="h-3 w-3 mr-1" />
                  Non-Compliant
                </Badge>
              )}
            </div>

            {compliance.key_rotation_status && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Key Rotation</span>
                <Badge
                  variant={
                    compliance.key_rotation_status === "ok"
                      ? "secondary"
                      : compliance.key_rotation_status === "warning"
                      ? "outline"
                      : "destructive"
                  }
                >
                  {compliance.key_rotation_status === "ok" && "Up to Date"}
                  {compliance.key_rotation_status === "warning" && "Due Soon"}
                  {compliance.key_rotation_status === "overdue" && "Overdue"}
                  {compliance.key_rotation_status === "no_key" && "No Key"}
                  {compliance.key_rotation_status === "no_policy" && "No Policy"}
                </Badge>
              </div>
            )}

            {compliance.days_until_rotation_required !== null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Days Until Rotation Required</span>
                  <span className="font-medium">{compliance.days_until_rotation_required} days</span>
                </div>
                <Progress
                  value={Math.max(0, Math.min(100, (compliance.days_until_rotation_required / 90) * 100))}
                  className="h-2"
                />
              </div>
            )}

            {compliance.issues && compliance.issues.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Issues</Label>
                <div className="space-y-2">
                  {compliance.issues.map((issue, idx) => {
                    // Handle both string and object issue formats
                    const issueText = typeof issue === 'string' ? issue : (issue as any).message || String(issue);
                    const isWarning = issueText.toLowerCase().includes("warning") || issueText.toLowerCase().includes("due in");
                    return (
                      <Alert
                        key={idx}
                        variant={isWarning ? "default" : "destructive"}
                      >
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{issueText}</AlertDescription>
                      </Alert>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Compliance Mode Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Compliance Mode
          </CardTitle>
          <CardDescription>
            Select the compliance framework to enforce
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Compliance Framework</Label>
            <Select value={complianceMode} onValueChange={(v) => setComplianceMode(v as ComplianceMode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(COMPLIANCE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {COMPLIANCE_DESCRIPTIONS[complianceMode]}
            </p>
          </div>

          {complianceMode !== "none" && (
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertTitle>Compliance Mode Active</AlertTitle>
              <AlertDescription>
                When compliance mode is enabled, certain security controls are enforced automatically.
                {complianceMode === "hipaa" && " HIPAA requires strict access controls and audit logging."}
                {complianceMode === "soc2" && " SOC 2 requires comprehensive security monitoring."}
                {complianceMode === "pci_dss" && " PCI-DSS requires annual key rotation."}
                {complianceMode === "gdpr" && " GDPR requires data protection and privacy controls."}
                {complianceMode === "hipaa_soc2" && " Combined compliance enforces all HIPAA and SOC 2 requirements."}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Security Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Security Controls</CardTitle>
          <CardDescription>
            Configure additional security requirements
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Require Audit Logging</Label>
              <p className="text-sm text-muted-foreground">
                Log all encryption key operations for compliance
              </p>
            </div>
            <Switch
              checked={requireAuditLogging}
              onCheckedChange={setRequireAuditLogging}
              disabled={complianceMode !== "none"}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Require Key Escrow</Label>
              <p className="text-sm text-muted-foreground">
                Store encrypted key copies for recovery purposes
              </p>
            </div>
            <Switch checked={requireKeyEscrow} onCheckedChange={setRequireKeyEscrow} />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Require Dual Control</Label>
              <p className="text-sm text-muted-foreground">
                Require two administrators for key operations
              </p>
            </div>
            <Switch checked={requireDualControl} onCheckedChange={setRequireDualControl} />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Allow Key Export</Label>
              <p className="text-sm text-muted-foreground">
                Allow exporting encryption keys (not recommended)
              </p>
            </div>
            <Switch checked={allowKeyExport} onCheckedChange={setAllowKeyExport} />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Require HSM</Label>
              <p className="text-sm text-muted-foreground">
                Require Hardware Security Module for key storage
              </p>
            </div>
            <Switch checked={requireHsm} onCheckedChange={setRequireHsm} />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={upsertMutation.isPending}>
          {upsertMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>

      <Separator />

      {/* Audit Log */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Encryption Audit Log
              </CardTitle>
              <CardDescription>
                Recent encryption-related events and operations
              </CardDescription>
            </div>
            {pendingReviewCount > 0 && (
              <Badge variant="destructive">
                {pendingReviewCount} pending review
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {auditLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : auditLog && auditLog.length > 0 ? (
            <AuditLogTable
              entries={auditLog}
              onReview={handleReviewEntry}
              isReviewing={reviewMutation.isPending}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No audit log entries</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
